import { audienceLabel, CARD_TYPE_LABEL } from './labels'
import {
  hasException,
  operationAssigned,
  operationAssignedByDefault
} from './effective'
import {
  AssignmentException,
  BrowseAndGoState,
  CardType,
  ClientPro,
  ExceptionAction,
  MultiChoiceCardModel,
  OperationModel
} from './types'

export interface AssignmentRow {
  key: string
  objectLabel: string
  title: string
  detail: string
  applied: boolean
  applyLabel: string
  revertLabel: string
  tone: 'danger' | 'default'
  warning?: string
  nested?: boolean
  target: 'cards' | 'operation'
  action: ExceptionAction
  ids: string[]
}

export interface AssignmentGroup {
  id: string
  title: string
  hint?: string
  rows: AssignmentRow[]
}

export interface AssignmentView {
  tags: string[]
  removeGroups: AssignmentGroup[]
  addGroups: AssignmentGroup[]
}

function cardsOf(state: BrowseAndGoState, operationId: string) {
  return state.cards.filter(card => card.operationModelId === operationId)
}

function ofType(operations: OperationModel[], state: BrowseAndGoState, type: CardType, defaultActive: boolean) {
  return operations.flatMap(operation =>
    cardsOf(state, operation.id).filter(card => card.type === type && card.defaultActive === defaultActive)
  )
}

function cardRow(
  key: string,
  cards: MultiChoiceCardModel[],
  action: ExceptionAction,
  client: ClientPro,
  exceptions: AssignmentException[],
  nested = false
): AssignmentRow {
  const type = cards[0].type
  const applied = cards.every(card =>
    hasException(exceptions, client.id, 'MULTICHOICE_CARD_MODEL', card.id, action)
  )
  const base = action === 'REMOVE'
    ? (applied ? 'Retirée pour ce client. Le modèle d\'opération reste affecté.' : 'Active par défaut')
    : (applied ? 'Ajoutée pour ce client' : 'Inactive par défaut')
  const detail = cards.length > 1 ? `${base} (${cards.length} modèles de carte multi-choix)` : base
  let applyLabel = 'Retirer'
  let revertLabel = 'Réactiver'
  if (action === 'ADD') {
    applyLabel = type === 'NO_CB' ? 'Ajouter le Sans CB' : 'Ajouter'
    revertLabel = 'Retirer'
  } else if (type === 'MIXED') {
    applyLabel = 'Retirer le Mixte'
  } else if (type === 'CB_ONLY') {
    applyLabel = 'Retirer la CB seule'
  } else {
    applyLabel = 'Retirer le Sans CB'
  }

  return {
    key,
    objectLabel: 'Modèle de carte multi-choix',
    title: cards.length === 1 ? cards[0].name : CARD_TYPE_LABEL[type],
    detail,
    applied,
    applyLabel,
    revertLabel,
    tone: action === 'REMOVE' && !applied ? 'danger' : 'default',
    nested,
    target: 'cards',
    action,
    ids: cards.map(card => card.id)
  }
}

function operationRow(
  operation: OperationModel,
  action: ExceptionAction,
  client: ClientPro,
  exceptions: AssignmentException[],
  deactivate: boolean
): AssignmentRow {
  const assigned = operationAssigned(operation, client, exceptions)
  const applied = action === 'REMOVE' ? !assigned : assigned
  let applyLabel = 'Ajouter'
  let revertLabel = 'Retirer'
  let detail = applied ? 'Affecté spécifiquement à ce client' : 'Non affecté par défaut'

  if (action === 'REMOVE' && deactivate) {
    applyLabel = 'Désactiver'
    revertLabel = 'Réactiver'
    detail = applied
      ? 'Désactivée pour ce client. Les modèles de carte multi-choix associés sont indisponibles.'
      : audienceLabel(operation.defaultAudience)
  } else if (action === 'REMOVE') {
    applyLabel = 'Retirer le modèle d\'opération'
    revertLabel = 'Réaffecter'
    detail = applied
      ? 'Retiré pour ce client. Les modèles de carte multi-choix associés sont indisponibles.'
      : audienceLabel(operation.defaultAudience)
  }

  return {
    key: `${action}-${operation.id}`,
    objectLabel: 'Modèle d\'opération',
    title: operation.name,
    detail,
    applied,
    applyLabel,
    revertLabel,
    tone: action === 'REMOVE' && !applied ? 'danger' : 'default',
    warning: operation.id === 'op-incentive' && client.cseOrRelated
      ? 'Ce type d\'opération ne doit pas être utilisé pour les dotations URSSAF.'
      : operation.adminWarning && action === 'ADD' ? operation.adminWarning : undefined,
    target: 'operation',
    action,
    ids: [operation.id]
  }
}

function cardAdjustments(
  state: BrowseAndGoState,
  operation: OperationModel,
  client: ClientPro,
  prefix: string
): AssignmentRow[] {
  const cards = cardsOf(state, operation.id)
  const rows: AssignmentRow[] = []
  const types: CardType[] = ['MIXED', 'CB_ONLY', 'NO_CB']
  types.forEach(type => {
    const active = cards.filter(card => card.type === type && card.defaultActive)
    const inactive = cards.filter(card => card.type === type && !card.defaultActive)
    if (active.length) rows.push(cardRow(`${prefix}-${type}-remove`, active, 'REMOVE', client, state.exceptions, true))
    if (inactive.length) rows.push(cardRow(`${prefix}-${type}-add`, inactive, 'ADD', client, state.exceptions, true))
  })
  return rows
}

export function buildAssignmentView(state: BrowseAndGoState, client: ClientPro): AssignmentView {
  const removeGroups: AssignmentGroup[] = []
  const addGroups: AssignmentGroup[] = []
  const exceptions = state.exceptions

  if (client.cseOrRelated) {
    const urssaf = state.operations.filter(operation => operation.family === 'URSSAF')
    const urssafAssigned = urssaf.filter(operation => operationAssigned(operation, client, exceptions))

    if (urssafAssigned.length) {
      const rows: AssignmentRow[] = []
      const mixed = ofType(urssaf, state, 'MIXED', true)
      const cbOnly = ofType(urssaf, state, 'CB_ONLY', true)
      if (mixed.length) rows.push(cardRow('cse-urssaf-mixed', mixed, 'REMOVE', client, exceptions))
      if (cbOnly.length) rows.push(cardRow('cse-urssaf-cb', cbOnly, 'REMOVE', client, exceptions))
      if (rows.length) {
        removeGroups.push({
          id: 'urssaf-cards',
          title: 'Cartes multi-choix URSSAF',
          hint: 'Le retrait porte sur le modèle de carte multi-choix. Le modèle d\'opération reste affecté.',
          rows
        })
      }
    }

    removeGroups.push({
      id: 'urssaf-operations',
      title: 'Modèles d\'opération URSSAF',
      hint: 'Désactiver un modèle d\'opération retire aussi ses modèles de carte multi-choix pour ce client.',
      rows: urssaf.map(operation => operationRow(operation, 'REMOVE', client, exceptions, true))
    })

    if (urssafAssigned.length) {
      const noCb = ofType(urssaf, state, 'NO_CB', false)
      if (noCb.length) {
        addGroups.push({
          id: 'urssaf-nocb',
          title: 'Carte multi-choix non affectée par défaut',
          rows: [cardRow('cse-urssaf-nocb', noCb, 'ADD', client, exceptions)]
        })
      }
    }

    const extraRows: AssignmentRow[] = []
    state.operations
      .filter(operation => operation.family !== 'URSSAF' && !operationAssignedByDefault(operation, client))
      .forEach(operation => {
        extraRows.push(operationRow(operation, 'ADD', client, exceptions, false))
        if (operationAssigned(operation, client, exceptions)) {
          extraRows.push(...cardAdjustments(state, operation, client, `extra-${operation.id}`))
        }
      })
    if (extraRows.length) {
      addGroups.push({
        id: 'cse-extra-operations',
        title: 'Modèles d\'opération non affectés par défaut',
        rows: extraRows
      })
    }
  } else {
    const incentive = state.operations.find(operation => operation.id === 'op-incentive')
    const others = state.operations.filter(operation => operation.family !== 'URSSAF' && operation.id !== 'op-incentive')

    if (incentive && operationAssigned(incentive, client, exceptions)) {
      const rows: AssignmentRow[] = []
      const mixed = ofType([incentive], state, 'MIXED', true)
      const cbOnly = ofType([incentive], state, 'CB_ONLY', true)
      if (mixed.length) rows.push(cardRow('incentive-mixed', mixed, 'REMOVE', client, exceptions))
      if (cbOnly.length) rows.push(cardRow('incentive-cb', cbOnly, 'REMOVE', client, exceptions))
      if (rows.length) {
        removeGroups.push({
          id: 'incentive-cards',
          title: 'Cartes multi-choix Incentive',
          hint: 'Le retrait porte sur le modèle de carte multi-choix. Le modèle d\'opération Incentive reste affecté.',
          rows
        })
      }
    }

    if (incentive) {
      removeGroups.push({
        id: 'incentive-operation',
        title: 'Modèle d\'opération',
        rows: [operationRow(incentive, 'REMOVE', client, exceptions, false)]
      })
    }

    others
      .filter(operation => operationAssignedByDefault(operation, client))
      .forEach(operation => {
        const rows: AssignmentRow[] = []
        if (operationAssigned(operation, client, exceptions)) {
          rows.push(
            ...cardAdjustments(state, operation, client, `def-${operation.id}`)
              .filter(row => row.action === 'REMOVE')
          )
        }
        rows.push(operationRow(operation, 'REMOVE', client, exceptions, false))
        removeGroups.push({ id: `remove-${operation.id}`, title: operation.name, rows })
      })

    const addCardRows: AssignmentRow[] = []
    if (incentive && operationAssigned(incentive, client, exceptions)) {
      const noCb = ofType([incentive], state, 'NO_CB', false)
      if (noCb.length) addCardRows.push(cardRow('incentive-nocb', noCb, 'ADD', client, exceptions))
    }
    if (addCardRows.length) {
      addGroups.push({
        id: 'incentive-nocb',
        title: 'Carte multi-choix Incentive non affectée par défaut',
        rows: addCardRows
      })
    }

    const addOpRows: AssignmentRow[] = []
    others
      .filter(operation => !operationAssignedByDefault(operation, client))
      .forEach(operation => {
        addOpRows.push(operationRow(operation, 'ADD', client, exceptions, false))
        if (operationAssigned(operation, client, exceptions)) {
          addOpRows.push(...cardAdjustments(state, operation, client, `add-${operation.id}`))
        }
      })
    if (addOpRows.length) {
      addGroups.push({
        id: 'non-cse-extra-operations',
        title: 'Modèles d\'opération non affectés par défaut',
        rows: addOpRows
      })
    }
  }

  return {
    tags: [
      `CSE et apparenté : ${client.cseOrRelated ? 'Oui' : 'Non'}`,
      client.cseOrRelated
        ? 'Opérations URSSAF affectées par défaut'
        : 'Incentive affecté par défaut'
    ],
    removeGroups,
    addGroups
  }
}
